import React, { useEffect, useRef, useState } from 'react';
import { Apple, Download, Laptop, Monitor, Upload } from 'lucide-react';
import syncApi from '../api/sync';
import { errorMessage } from '../api/drive';
import { useAuth } from '../contexts/AuthProvider';
import { useDrive } from '../contexts/DriveProvider';
import { formatDate, formatSize } from '../lib/files';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';
import { startDownload } from '../components/drive/useItemActions';

const detectOS = () => {
  const p = (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '').toLowerCase();
  if (p.includes('win')) return 'windows';
  if (p.includes('mac')) return 'mac';
  return 'mac';
};

const PLATFORMS = {
  mac: { label: 'Mac', icon: Apple, file: '.dmg', needs: 'macOS 11 Big Sur or newer, Apple Silicon or Intel' },
  windows: { label: 'Windows', icon: Monitor, file: '.exe', needs: 'Windows 10 or 11, 64-bit' },
};

const STEPS = {
  mac: [
    { title: 'Download the app', body: <>Click <b>Download for Mac</b> above. You get a file called <code>LampTwist.Drive_…_universal.dmg</code>.</> },
    { title: 'Move it to Applications', body: <>Open the downloaded file and drag <b>LampTwist Drive</b> onto the <b>Applications</b> folder in the window that appears (follow the amber arrow).</> },
    {
      title: 'Open it the first time',
      body: (
        <>
          The app isn’t from the App Store, so macOS asks once. Open <b>Applications</b>, <b>right-click</b> LampTwist Drive and choose <b>Open</b>, then <b>Open</b> again.
          <span className="mt-1.5 block text-muted-foreground">
            On macOS 15 or newer you get <i>“Apple could not verify…”</i> (Dutch: <i>“Apple kon niet verifiëren…”</i>) with only a <b>Done</b> (<b>Gereed</b>) button.
            Click it, open <b>System Settings → Privacy &amp; Security</b> (<b>Systeeminstellingen → Privacy en beveiliging</b>), scroll down and click
            <b> Open Anyway</b> (<b>Toch openen</b>) next to LampTwist Drive, enter your Mac password and confirm. The button only shows for about an hour after trying to open the app.
          </span>
          <span className="mt-1.5 block text-muted-foreground">
            No button there (company-managed Macs)? Paste this in Terminal, then open the app normally:{' '}
            <code>xattr -dr com.apple.quarantine &quot;/Applications/LampTwist Drive.app&quot;</code>
          </span>
        </>
      ),
    },
    { title: 'Sign in', body: <>Click <b>Sign in with your LampTwist account</b>. Your browser opens — check that the code matches the one in the app and click <b>Allow</b>.</> },
    { title: 'You’re set', body: <>The LampTwist Drive icon appears in the <b>menu bar</b> (top right — there’s no Dock icon). <b>Status</b> shows what synced; <b>Browse</b> shows everything on Drive without downloading it — double-click a file to open it. Synced folders live in <b>LampTwist Drive</b> in your home folder. From now on the app updates itself.</> },
  ],
  windows: [
    { title: 'Download the app', body: <>Click <b>Download for Windows</b> above. You get a file called <code>LampTwist.Drive_…_x64-setup.exe</code>.</> },
    {
      title: 'Run the installer',
      body: (
        <>
          Open the downloaded file. Windows may show a blue <b>“Windows protected your PC”</b> screen because the app is new — click <b>More info</b>, then <b>Run anyway</b>.
          <span className="mt-1.5 block text-muted-foreground">It installs for your user only, no administrator rights needed.</span>
        </>
      ),
    },
    { title: 'Sign in', body: <>LampTwist Drive opens by itself. Click <b>Sign in with your LampTwist account</b>, check the code in your browser and click <b>Allow</b>.</> },
    { title: 'You’re syncing', body: <>The icon sits in the system tray next to the clock — click the <b>^</b> arrow if you don’t see it, and drag it onto the taskbar to keep it visible. Your files are in <code>C:\Users\&lt;you&gt;\LampTwist Drive</code>.</> },
  ],
};

const FAQ = [
  ['What gets synced?', 'Your My Drive, to start. Click “Choose folders” in the app to add shared drives, pick only certain folders, or untick everything. Synced items work offline and update by themselves.'],
  ['Can I open a file without syncing its folder?', 'Yes. In the app’s Browse tab double-click any file, or on drive.lamptwist.be right-click a file → “Open on this computer”. You get a temporary copy: save as usual and your changes upload to Drive. Copies you haven’t opened for 7 days are cleaned up (never while changes are waiting to upload). Use “Keep this folder on this computer” in Browse for folders you need offline.'],
  ['Do I need to update the app?', 'No — from version 0.2.0 it checks for new versions in the background, installs them and restarts itself when nothing is syncing. Version 0.1.0 has to be replaced by hand once.'],
  ['What if two people edit the same file?', 'Nothing is lost: Drive keeps the first saved version, and the other one is saved next to it as “… (conflicted copy, <computer>, <time>)”. Compare them and keep what you need.'],
  ['What happens when I delete a file?', 'Deleting in the folder moves the file to Drive’s Trash, where it can be restored for everyone. Files deleted by colleagues go to your computer’s Trash / Recycle Bin, never straight into nothingness.'],
  ['Can I edit everything?', 'Only what you could edit on the web. In view-only folders your local changes are kept as a separate copy on your computer and not uploaded.'],
  ['My laptop is lost or I’m changing computers', 'Sign that computer out under “Your computers” below — it stops syncing immediately. On a new computer just install and sign in again.'],
  ['How do I stop or remove it?', 'Use Pause in the app, or Settings → Sign out. To remove it entirely: Mac — quit it and drag it from Applications to the Trash; Windows — Settings → Apps → LampTwist Drive → Uninstall. Your LampTwist Drive folder stays on the computer.'],
];

const PublishForm = ({ onPublished }) => {
  const { toast } = useDrive();
  const [platform, setPlatform] = useState('mac');
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [progress, setProgress] = useState(null);
  const inputRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setProgress(0);
    try {
      await syncApi.publish(platform, file, version.trim(), setProgress);
      toast(`New ${PLATFORMS[platform].label} version published`);
      setFile(null);
      setVersion('');
      if (inputRef.current) inputRef.current.value = '';
      onPublished();
    } catch (err) {
      toast(errorMessage(err, 'Could not publish this file.'), 'error');
    } finally {
      setProgress(null);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-dashed border-border p-4">
      <div>
        <b className="text-[13.5px]">Publish a new version</b>
        <p className="text-[13px] text-muted-foreground">Admins only. Upload the installer from the GitHub release (or the “LampTwist Drive installers” folder). Everyone gets it here right away.</p>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-muted-foreground">Platform</span>
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-9 rounded-lg border border-border bg-background px-2">
            <option value="mac">Mac (.dmg)</option>
            <option value="windows">Windows (.exe)</option>
          </select>
        </label>
        <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-[13px]">
          <span className="text-muted-foreground">Installer file</span>
          <input
            ref={inputRef}
            type="file"
            accept={platform === 'mac' ? '.dmg' : '.exe,.msi'}
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="h-9 rounded-lg border border-border bg-background px-2 py-1.5 text-[13px] file:mr-2 file:rounded file:border-0 file:bg-muted file:px-2 file:text-[12.5px]"
          />
        </label>
        <label className="flex w-28 flex-col gap-1 text-[13px]">
          <span className="text-muted-foreground">Version</span>
          <input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="auto" className="h-9 rounded-lg border border-border bg-background px-2" />
        </label>
        <button type="submit" disabled={!file || progress !== null} className="flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-medium text-primary-foreground hover:bg-primary/85 disabled:opacity-50">
          <Upload className="h-4 w-4" aria-hidden="true" />
          {progress === null ? 'Publish' : `Uploading ${Math.round(progress * 100)}%`}
        </button>
      </div>
    </form>
  );
};

/** Download the desktop app, install it step by step, and manage signed-in computers. */
const DesktopPage = () => {
  const { user } = useAuth();
  const { toast } = useDrive();
  const [releases, setReleases] = useState(null);
  const [devices, setDevices] = useState(null);
  const [os, setOs] = useState(detectOS);

  const loadReleases = () => syncApi.downloads().then(setReleases).catch(() => setReleases({}));
  const loadDevices = () => syncApi.devices().then(setDevices).catch(() => setDevices([]));
  useEffect(() => { loadReleases(); loadDevices(); }, []);

  const download = async (platform) => {
    try {
      startDownload(await syncApi.downloadUrl(platform));
    } catch (err) {
      toast(errorMessage(err, 'Download failed.'), 'error');
    }
  };

  const revoke = async (d) => {
    try {
      await syncApi.revoke(d.id);
      toast(`“${d.name}” signed out`);
      loadDevices();
    } catch (err) {
      toast(errorMessage(err, 'Could not sign out this computer.'), 'error');
    }
  };

  const ordered = os === 'windows' ? ['windows', 'mac'] : ['mac', 'windows'];

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader><PageTitle>Desktop app</PageTitle></PageHeader>
      <div className="flex w-full max-w-[1400px] flex-col gap-10 px-4 py-6 sm:px-6 lg:px-8">

        <section className="flex flex-col gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-[-0.01em]">LampTwist Drive on your computer</h2>
            <p className="mt-1 max-w-[80ch] text-[14px] text-muted-foreground">
              Keep the drives and folders you choose in a <b className="text-foreground">LampTwist Drive</b> folder on your Mac or PC —
              available offline, updated automatically, and anything you save there uploads by itself.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {ordered.map((key, i) => {
              const p = PLATFORMS[key];
              const r = releases?.[key];
              return (
                <div key={key} className={`flex min-w-[260px] flex-1 flex-col gap-2 rounded-xl border p-5 ${i === 0 ? 'border-filament bg-accent/40' : 'border-border'}`}>
                  <div className="flex items-center gap-2">
                    <p.icon className="h-5 w-5" aria-hidden="true" />
                    <b>{p.label}</b>
                    {i === 0 && <span className="ml-auto rounded-md bg-filament px-1.5 py-0.5 text-[11px] font-semibold text-filament-foreground">Your computer</span>}
                  </div>
                  <span className="text-[12.5px] text-muted-foreground">{p.needs}</span>
                  {releases === null ? (
                    <div className="h-9 animate-pulse rounded-lg bg-muted" />
                  ) : r ? (
                    <>
                      <button
                        type="button"
                        onClick={() => download(key)}
                        className={`flex h-9 items-center justify-center gap-2 rounded-lg text-[13.5px] font-medium ${i === 0 ? 'bg-primary text-primary-foreground hover:bg-primary/85' : 'border border-border hover:bg-muted'}`}
                      >
                        <Download className="h-4 w-4" aria-hidden="true" /> Download for {p.label}
                      </button>
                      <span className="text-xs text-muted-foreground">Version {r.version}, {formatSize(r.size)}, published {formatDate(r.uploaded_at)}</span>
                    </>
                  ) : (
                    <span className="flex h-9 items-center justify-center rounded-lg border border-dashed border-border text-[13px] text-muted-foreground">Not published yet</span>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <div className="grid grid-cols-1 gap-10 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] xl:gap-14">
        <section className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-base font-semibold">How to install</h2>
            <div role="tablist" aria-label="Operating system" className="flex gap-0.5 rounded-lg border border-border p-0.5">
              {['mac', 'windows'].map((k) => (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  aria-selected={os === k}
                  onClick={() => setOs(k)}
                  className={`h-7 rounded-md px-3 text-[13px] font-medium ${os === k ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                >
                  {PLATFORMS[k].label}
                </button>
              ))}
            </div>
          </div>
          <ol className="flex flex-col gap-4">
            {STEPS[os].map((s, i) => (
              <li key={s.title} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[12px] font-semibold">{i + 1}</span>
                <div className="flex flex-col gap-0.5 text-[14px]">
                  <b>{s.title}</b>
                  <span className="text-foreground/90 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:text-[12.5px]">{s.body}</span>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <div className="flex flex-col gap-10">
        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">Good to know</h2>
          <div className="flex flex-col divide-y divide-border border-y border-border">
            {FAQ.map(([q, a]) => (
              <details key={q} className="group py-3">
                <summary className="cursor-pointer list-none text-[14px] font-medium marker:hidden">
                  <span className="mr-2 inline-block text-muted-foreground transition-transform group-open:rotate-90">›</span>{q}
                </summary>
                <p className="mt-1.5 pl-4 text-[13.5px] text-muted-foreground">{a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-2">
          <h2 className="text-base font-semibold">Your computers</h2>
          {devices === null && <div className="h-14 animate-pulse rounded-lg bg-muted" />}
          {devices?.length === 0 && <p className="text-[13.5px] text-muted-foreground">No computers yet. Install the app and sign in from it.</p>}
          <ul className="flex flex-col divide-y divide-border border-y border-border empty:hidden">
            {devices?.map((d) => {
              const Icon = d.platform === 'windows' ? Monitor : Laptop;
              return (
                <li key={d.id} className="flex items-center gap-3 py-3">
                  <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-[13.5px] font-medium">{d.name}</span>
                    <span className="text-xs text-muted-foreground">
                      Signed in {formatDate(d.created_at)}{d.last_seen_at ? `, last synced ${formatDate(d.last_seen_at).replace(/^Today/, 'today')}` : ''}
                    </span>
                  </div>
                  <button type="button" onClick={() => revoke(d)} className="h-8 rounded-lg px-2.5 text-[13px] font-medium text-destructive hover:bg-destructive/10">
                    Sign out
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        </div>
        </div>

        {user?.is_staff && <PublishForm onPublished={loadReleases} />}
      </div>
    </div>
  );
};

export default DesktopPage;
